import { h as _objectWithoutProperties, _ as _objectSpread2, a as _slicedToArray, I as IntersectionObserver, j as _taggedTemplateLiteral } from './common.js';
import { jsx } from '@emotion/react/jsx-runtime';
import { memo, useCallback, useState, useEffect } from 'react';
import { useTheme, css, keyframes } from '@emotion/react';
import { i as useNodeSizeObserver } from './hooks.js';
import 'qs';
import '@bucketplace/lib';
import { e as calcImageSize, d as getNearestSize, b as getImageUrl, c as getImageSrcSet } from './utils.js';
import { R as RESET_BUTTON, p as pxToRem } from './foundation.js';

var _excluded$2 = ["src", "alt", "style", "className", "quality", "objectFit", "placeholderColor", "intrinsicInitialWidth", "intrinsicInitialHeight", "intrinsicAspect", "lazy", "onError"];
var DEFAULT_WIDTH = 240;
var PIXEL_RATIOS = [1.5, 2, 3];
var DEFAULT_RESIZE_IMAGE = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAQSURBVHgBAQUA+v8AAAAAAAAFAAFkeJU4AAAAAElFTkSuQmCC';
var ResizeImage = /*#__PURE__*/memo(function (_ref) {
  var src = _ref.src,
    alt = _ref.alt,
    style = _ref.style,
    className = _ref.className,
    quality = _ref.quality,
    objectFit = _ref.objectFit,
    placeholderColor = _ref.placeholderColor,
    _ref$intrinsicInitial = _ref.intrinsicInitialWidth,
    intrinsicInitialWidth = _ref$intrinsicInitial === void 0 ? DEFAULT_WIDTH : _ref$intrinsicInitial,
    intrinsicInitialHeight = _ref.intrinsicInitialHeight,
    intrinsicAspect = _ref.intrinsicAspect,
    _ref$lazy = _ref.lazy,
    lazy = _ref$lazy === void 0 ? {
      threshold: 0
    } : _ref$lazy,
    onError = _ref.onError,
    rest = _objectWithoutProperties(_ref, _excluded$2);
  var theme = useTheme();
  var getImageOptions = useCallback(function () {
    return _objectSpread2(_objectSpread2({
      src: src,
      quality: quality
    }, calcImageSize({
      width: intrinsicInitialWidth,
      height: intrinsicInitialHeight,
      aspect: intrinsicAspect
    })), intrinsicAspect && {
      mode: 'cover'
    });
  }, [src, intrinsicAspect, intrinsicInitialWidth, intrinsicInitialHeight, quality]);
  var _useState = useState(function () {
      return getImageOptions();
    }),
    _useState2 = _slicedToArray(_useState, 2),
    image = _useState2[0],
    setImage = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isLoaded = _useState4[0],
    setLoaded = _useState4[1];
  useEffect(function () {
    setImage(getImageOptions());
    setLoaded(false);
  }, [src, intrinsicAspect, getImageOptions]);
  var handleChangeImgNodeSize = useCallback(function (size) {
    if (size.width === 0 || size.height === 0 || !isLoaded) return;
    setImage(function (image) {
      if (size.width > (image.width || 0) || size.height > (image.height || 0)) {
        var width = getNearestSize(size.width);
        var height = Math.ceil(width * size.height / size.width);
        return _objectSpread2(_objectSpread2(_objectSpread2({}, image), typeof image.width !== 'undefined' && {
          width: width
        }), typeof image.height !== 'undefined' && {
          height: height
        });
      }
      return image;
    });
  }, [isLoaded]);
  var imgNodeRef = useNodeSizeObserver(handleChangeImgNodeSize, [isLoaded]);
  var imgCSS = /*#__PURE__*/css(objectFit && /*#__PURE__*/css("width:100%;height:100%;object-fit:", objectFit, ";" + (process.env.NODE_ENV === "production" ? "" : ";label:imgCSS;")), " ", !isLoaded && /*#__PURE__*/css("background-color:", placeholderColor || theme.content.base4, ";" + (process.env.NODE_ENV === "production" ? "" : ";label:imgCSS;")), ";" + (process.env.NODE_ENV === "production" ? "" : ";label:imgCSS;"));
  var _useState5 = useState(!lazy),
    _useState6 = _slicedToArray(_useState5, 2),
    isLazyRendered = _useState6[0],
    setLazyRendered = _useState6[1];
  if (!isLazyRendered) {
    return jsx(IntersectionObserver, _objectSpread2(_objectSpread2({
      onIntersect: function onIntersect() {
        return setLazyRendered(true);
      }
    }, lazy), {}, {
      style: style,
      className: className,
      children: jsx("img", {
        // 1x1 transparent image
        src: DEFAULT_RESIZE_IMAGE,
        style: style,
        css: imgCSS,
        className: className,
        alt: "",
        "aria-label": rest['aria-label']
      })
    }));
  }
  var imageSrc = getImageUrl(image);
  var imageSrcSet = getImageSrcSet(image, PIXEL_RATIOS);
  return jsx("img", {
    src: imageSrc,
    srcSet: imageSrcSet,
    ref: imgNodeRef,
    alt: alt,
    style: style,
    css: imgCSS,
    className: className,
    onError: onError,
    onLoad: function onLoad() {
      return setLoaded(true);
    },
    "aria-label": rest['aria-label']
  });
});

var _excluded$1 = ["maxPage", "currentPage", "onClick"];
function _EMOTION_STRINGIFIED_CSS_ERROR__() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }
function cssDotColors(_ref3) {
  var dotBackgroundColor = _ref3.dotBackgroundColor,
    boxShadow = _ref3.boxShadow;
  return /*#__PURE__*/css("background-color:", dotBackgroundColor, ";box-shadow:", boxShadow, ";" + (process.env.NODE_ENV === "production" ? "" : ";label:cssDotColors;"));
}
function cssCurrentDot(_ref4) {
  var activeDotBackgroundColor = _ref4.activeDotBackgroundColor,
    boxShadow = _ref4.boxShadow;
  return /*#__PURE__*/css("background-color:", activeDotBackgroundColor, ";box-shadow:", boxShadow, ";" + (process.env.NODE_ENV === "production" ? "" : ";label:cssCurrentDot;"));
}
function cssDotSize(size) {
  return /*#__PURE__*/css("width:", pxToRem(size), ";height:", pxToRem(size), ";", size === 0 ? "margin: ".concat(pxToRem(0)) : '', ";" + (process.env.NODE_ENV === "production" ? "" : ";label:cssDotSize;"));
}
var _ref = process.env.NODE_ENV === "production" ? {
  name: "wv42kf",
  styles: "margin:0;padding:0;height:0;vertical-align:bottom;display:inline-block"
} : {
  name: "wpwjfm-css",
  styles: "margin:0;padding:0;height:0;vertical-align:bottom;display:inline-block;label:css;",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__
};
var _ref2 = process.env.NODE_ENV === "production" ? {
  name: "ofztlr",
  styles: "text-align:center;white-space:nowrap;font-size:0;line-height:0;padding:0"
} : {
  name: "ycgtt0-css",
  styles: "text-align:center;white-space:nowrap;font-size:0;line-height:0;padding:0;label:css;",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__
};
function makeIndicatorDots(settings) {
  var dotSize = settings.dotSize;
  return function (_ref5) {
    var maxPage = _ref5.maxPage,
      _ref5$currentPage = _ref5.currentPage,
      currentPage = _ref5$currentPage === void 0 ? 1 : _ref5$currentPage,
      _onClick = _ref5.onClick,
      rest = _objectWithoutProperties(_ref5, _excluded$1);
    var _useState = useState(currentPage - 1),
      _useState2 = _slicedToArray(_useState, 2),
      currentIndex = _useState2[0],
      setCurrentIndex = _useState2[1];
    var isCurrentDot = function isCurrentDot(index) {
      return index === currentIndex;
    };
    useEffect(function () {
      setCurrentIndex(currentPage - 1);
    }, [currentPage]);
    if (maxPage <= 1) return null;
    return jsx("ul", _objectSpread2(_objectSpread2({
      css: _ref2
    }, rest), {}, {
      children: Array(maxPage).fill(0).map(function (_, index) {
        return jsx("li", {
          css: _ref,
          children: jsx("button", {
            type: "button",
            "aria-label": "".concat(index + 1, " \uD398\uC774\uC9C0"),
            "aria-current": isCurrentDot(index) ? 'true' : undefined,
            onClick: function onClick() {
              setCurrentIndex(index);
              _onClick === null || _onClick === void 0 ? void 0 : _onClick(index);
            },
            css: [/*#__PURE__*/css(RESET_BUTTON, " border-radius:50%;transition:0.15s background-color,0.15s width,0.15s height;transform:translateY(-50%);box-sizing:border-box;margin:0 ", pxToRem(3), ";" + (process.env.NODE_ENV === "production" ? "" : ";label:css;")), cssDotColors(settings), isCurrentDot(index) && cssCurrentDot(settings), cssDotSize(dotSize(index, maxPage, currentIndex))]
          })
        }, "dot-".concat(index));
      })
    }));
  };
}

var _excluded = ["disabled", "variant", "onDimmed"];
var _templateObject;
var rotate = keyframes(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  from {\n    transform: rotate(0deg);\n  }\n  to {\n    transform: rotate(360deg);\n  }\n"])));
function cssVariant(theme, variant, onDimmed) {
  switch (variant) {
    case 'default':
      return /*#__PURE__*/css("border-color:", theme.dim.onDark.thick, ";border-top-color:", theme.content.white, ";" + (process.env.NODE_ENV === "production" ? "" : ";label:cssVariant;"));
    case 'white':
      return /*#__PURE__*/css("border-color:", theme.dim.onDark.basic, ";border-top-color:", theme.content.white, ";" + (process.env.NODE_ENV === "production" ? "" : ";label:cssVariant;"));
    case 'base1':
      return /*#__PURE__*/css("border-color:", onDimmed ? theme.content.base6 : theme.content.base5, ";border-top-color:", theme.content.base1, ";" + (process.env.NODE_ENV === "production" ? "" : ";label:cssVariant;"));
    case 'primary1':
    case 'primary2':
    case 'primary3':
    case 'primary4':
      return /*#__PURE__*/css("border-color:", onDimmed ? theme.content.base6 : theme.content[variant].thin, ";border-top-color:", theme.content[variant].basic, ";" + (process.env.NODE_ENV === "production" ? "" : ";label:cssVariant;"));
  }
}
function Loading(_ref) {
  var _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'default' : _ref$variant,
    _ref$onDimmed = _ref.onDimmed,
    onDimmed = _ref$onDimmed === void 0 ? false : _ref$onDimmed,
    rest = _objectWithoutProperties(_ref, _excluded);
  var theme = useTheme();
  return jsx("div", _objectSpread2(_objectSpread2({}, rest), {}, {
    css: /*#__PURE__*/css("display:inline-block;width:", pxToRem(24), ";height:", pxToRem(24), ";" + (process.env.NODE_ENV === "production" ? "" : ";label:css;")),
    children: jsx("div", {
      css: [/*#__PURE__*/css("border-radius:50%;width:100%;height:100%;border:", pxToRem(2), " solid;animation:", rotate, " 1.1s infinite linear;box-sizing:border-box;" + (process.env.NODE_ENV === "production" ? "" : ";label:css;")), disabled ? /*#__PURE__*/css("border-color:", theme.content.base3, ";border-top-color:", theme.content.white, ";" + (process.env.NODE_ENV === "production" ? "" : ";label:css;")) : cssVariant(theme, variant, onDimmed)]
    })
  }));
}

export { DEFAULT_RESIZE_IMAGE as D, Loading as L, ResizeImage as R, makeIndicatorDots as m };
//# sourceMappingURL=Loading.js.map
